import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { getTradedFacilities } from 'app/state/ducks/traded-facilities/selectors'
import { getSubTrades } from 'app/state/ducks/sub-trades/selectors'
import { getFacilityAmounts } from 'app/state/ducks/facilities/selectors'
import { getMasterTrades } from 'app/state/ducks/master-trades/selectors'
import {
  TradedFacilityParams,
  AmountsWithDate,
} from 'app/models/traded-facilities-params'
import { FacilityAmountData } from 'app/models/facility-params'
import { MasterTradeParams } from 'app/models/master-trade-params'

// Checks if there are unapplied facility transactions on the same date as this transaction
// Uses count-based matching: if facility count != trade count on a date, something is unapplied
export const hasUnappliedTransaction = (
  facilityTransaction: FacilityAmountData,
  allFacilityTransactions: FacilityAmountData[],
  appliedTradeAmounts?: AmountsWithDate[],
): boolean => {
  if (!appliedTradeAmounts) return false // Data not available, can't determine
  // A traded facility with an empty amounts array has no recorded transactions
  // (not even the initial trade creation). Any facility-level PIK/Paydown event
  // is therefore unapplied since nothing has been recorded against the trade.
  if (appliedTradeAmounts.length === 0) return true

  // Count facility transactions on the same date
  const facilityCountOnDate = allFacilityTransactions.filter(ft =>
    moment(ft.effectiveDate).isSame(
      moment(facilityTransaction.effectiveDate),
      'day',
    ),
  ).length

  // Count trade amounts on the same date (excluding initial trade creation)
  const tradeCountOnDate = appliedTradeAmounts.filter(
    ta =>
      ta.transaction !== 'TradeCreationTradeTransaction' &&
      moment(ta.effectiveDate).isSame(
        moment(facilityTransaction.effectiveDate),
        'day',
      ),
  ).length

  // If counts don't match, something is unapplied on this date
  return facilityCountOnDate !== tradeCountOnDate
}

// checks if a sub-trade has unapplied PIK or paydown transactions
const hasUnappliedTransactionsForSubTrade = (
  tradedFacilities: TradedFacilityParams[],
  facilityAmountsMap: Map<string, FacilityAmountData[]>,
  masterTrade: MasterTradeParams,
  subTrade: { lockedDate?: string },
): boolean => {
  for (const facility of tradedFacilities) {
    const facilityAmounts = facilityAmountsMap.get(facility.facilityId.id)
    if (!facilityAmounts || facilityAmounts.length === 0) continue

    const filteredAmounts = facilityAmounts.filter(
      (facilityTransaction: FacilityAmountData) =>
        new Date(facilityTransaction.effectiveDate) >=
          new Date(masterTrade.tradeDate ?? '') &&
        (!subTrade.lockedDate ||
          new Date(facilityTransaction.effectiveDate) <=
            new Date(subTrade.lockedDate)) &&
        ['FacilityPaydown', 'FacilityPIK'].includes(
          facilityTransaction.actionType,
        ),
    )

    for (const facilityTransaction of filteredAmounts) {
      if (
        hasUnappliedTransaction(
          facilityTransaction,
          filteredAmounts,
          facility.amounts,
        )
      ) {
        return true
      }
    }
  }

  return false
}

// Batch utility: pre-builds lookup maps once, then checks all trades in O(n) instead of O(n²)
// Returns a Set of master trade IDs that have unapplied transactions
export const buildUnappliedTradeIdSet = (
  masterTrades: MasterTradeParams[],
  allSubTrades: { masterTradeId: { id: string }; id?: string; lockedDate?: string }[],
  allTradedFacilities: TradedFacilityParams[],
  allFacilityAmounts: { facilityId: { id: string }; facilityAmounts: FacilityAmountData[] }[],
): Set<string> => {
  const result = new Set<string>()

  // Pre-build maps for O(1) lookups
  const subTradesByMasterTradeId = new Map<string, typeof allSubTrades>()
  for (const st of allSubTrades) {
    const mid = st.masterTradeId.id
    if (!subTradesByMasterTradeId.has(mid))
      subTradesByMasterTradeId.set(mid, [])
    subTradesByMasterTradeId.get(mid)!.push(st)
  }

  const tradedFacilitiesBySubTradeId = new Map<string, TradedFacilityParams[]>()
  for (const tf of allTradedFacilities) {
    const stId = tf.contractId?.id
    if (!stId) continue
    if (!tradedFacilitiesBySubTradeId.has(stId))
      tradedFacilitiesBySubTradeId.set(stId, [])
    tradedFacilitiesBySubTradeId.get(stId)!.push(tf)
  }

  const facilityAmountsByFacilityId = new Map<string, FacilityAmountData[]>()
  for (const fa of allFacilityAmounts) {
    if (fa.facilityAmounts.length > 0) {
      facilityAmountsByFacilityId.set(fa.facilityId.id, fa.facilityAmounts)
    }
  }

  // Check each master trade using pre-built maps
  for (const masterTrade of masterTrades) {
    const subTrades = subTradesByMasterTradeId.get(masterTrade.id ?? '') ?? []

    for (const subTrade of subTrades) {
      const tradedFacilities =
        tradedFacilitiesBySubTradeId.get(subTrade.id ?? '') ?? []
      if (tradedFacilities.length === 0) continue

      if (
        hasUnappliedTransactionsForSubTrade(
          tradedFacilities,
          facilityAmountsByFacilityId,
          masterTrade,
          { lockedDate: subTrade.lockedDate },
        )
      ) {
        result.add(masterTrade.id ?? '')
        break
      }
    }
  }

  return result
}

// Custom hook to check for unapplied payment/PIK transactions across all sub-trades in a master trade
export const useUnappliedTransactions = (
  masterTradeId: string | undefined,
): boolean => {
  const [hasUnapplied, setHasUnapplied] = useState(false)

  const allTradedFacilities = useSelector(getTradedFacilities)
  const allSubTrades = useSelector(getSubTrades)
  const facilityAmounts = useSelector(getFacilityAmounts)
  const allMasterTrades = useSelector(getMasterTrades)

  useEffect(() => {
    async function checkUnappliedTransactions() {
      if (!masterTradeId) {
        setHasUnapplied(false)
        return
      }

      const masterTrade = allMasterTrades.find(mt => mt.id === masterTradeId)
      if (!masterTrade || !allTradedFacilities.length) {
        setHasUnapplied(false)
        return
      }

      const subTradesForMasterTrade = allSubTrades.filter(
        st => st.masterTradeId.id === masterTradeId,
      )

      for (const subTrade of subTradesForMasterTrade) {
        const tradedFacilitiesForSubTrade = allTradedFacilities.filter(
          tf => tf.contractId?.id === subTrade.id,
        )

        if (tradedFacilitiesForSubTrade.length === 0) {
          continue
        }

        const facilityAmountsMap = new Map<string, FacilityAmountData[]>()
        tradedFacilitiesForSubTrade.forEach(facility => {
          const facilityId = facility.facilityId.id

          const facilityAmountsEntry = facilityAmounts.find(
            fa => fa.facilityId.id === facilityId,
          )
          if (
            facilityAmountsEntry &&
            facilityAmountsEntry.facilityAmounts.length > 0
          ) {
            facilityAmountsMap.set(
              facilityId,
              facilityAmountsEntry.facilityAmounts,
            )
          }
        })

        const hasUnappliedInSubTrade = hasUnappliedTransactionsForSubTrade(
          tradedFacilitiesForSubTrade,
          facilityAmountsMap,
          masterTrade,
          { lockedDate: subTrade.lockedDate },
        )

        if (hasUnappliedInSubTrade) {
          setHasUnapplied(true)
          return
        }
      }

      setHasUnapplied(false)
    }

    checkUnappliedTransactions()
  }, [
    masterTradeId,
    allTradedFacilities,
    allSubTrades,
    facilityAmounts,
    allMasterTrades,
  ])

  return hasUnapplied
}
